import type Luminary from '@lumen5/luminary';

import { ExclusiveQueue } from './ExclusiveQueue';
import { SubagentAccess } from './subagentAccess';
import { resolveSceneById } from '../tools/scene-addressing';
import { resolveSceneGroupById, sceneGroupDisplayId } from '../tools/scene-group-addressing';

import type { GetRootState } from 'js/src/libs/RootState';

/**
 * The scene groups one of several subagents running together may edit: the ones it was given,
 * plus any it creates. Scopes never overlap, so each subagent's edits are its own.
 */
export class SubagentScope {
    private readonly sceneGroupIds: Set<string>;

    constructor(
        private readonly luminary: Luminary,
        private readonly getState: GetRootState,
        sceneGroupIds: string[],
        /** Shared by every subagent started together; see `SubagentRegistry`. */
        public readonly queues: { capture: ExclusiveQueue; question: ExclusiveQueue } = {
            capture: new ExclusiveQueue(),
            question: new ExclusiveQueue(),
        },
    ) {
        this.sceneGroupIds = new Set(sceneGroupIds);
    }

    /** Every scene group this subagent has owned, including any it has since deleted. */
    public getSceneGroupIds(): string[] {
        return [...this.sceneGroupIds];
    }

    public owns(sceneGroupId: string): boolean {
        return this.sceneGroupIds.has(sceneGroupId);
    }

    /** Whether a lint finding is about this scope's scenes or scene groups; video-wide ones aren't. */
    public ownsFinding({
        sceneId,
        sceneGroupIndex,
    }: {
        sceneId?: string | string[];
        sceneGroupIndex?: number;
    }): boolean {
        if (sceneId !== undefined) {
            const sceneIds = Array.isArray(sceneId) ? sceneId : [sceneId];
            return sceneIds.some((id) => {
                const sceneGroupId = this.luminary.scenes.find((scene) => scene.id === id)
                    ?.sceneGroup?.id;
                return sceneGroupId !== undefined && this.owns(sceneGroupId);
            });
        }
        if (sceneGroupIndex !== undefined) {
            const sceneGroupId = this.luminary.sceneGroups[sceneGroupIndex]?.id;
            return sceneGroupId !== undefined && this.owns(sceneGroupId);
        }
        return false;
    }

    public claimSceneGroup(sceneGroupId: string): void {
        this.sceneGroupIds.add(sceneGroupId);
    }

    public claimSceneGroupOfScene(sceneId: string): void {
        const sceneGroupId = this.luminary.scenes.find((scene) => scene.id === sceneId)?.sceneGroup
            ?.id;
        if (sceneGroupId) {
            this.claimSceneGroup(sceneGroupId);
        }
    }

    public describe(): string {
        const ids = this.getSceneGroupIds().filter((id) => this.luminary.getSceneGroupById(id));
        return ids.length > 0
            ? ids.map((id) => sceneGroupDisplayId(this.luminary, id)).join(', ')
            : 'none yet';
    }

    /**
     * Why a tool call reaches outside this scope, or null when it doesn't. An id that doesn't
     * resolve passes, so the tool reports the bad id itself.
     */
    public checkToolCall(
        access: SubagentAccess,
        toolName: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args: Record<string, any>,
    ): string | null {
        switch (access) {
            case SubagentAccess.READ_ONLY:
            case SubagentAccess.CAPTURE:
            case SubagentAccess.ASKS_USER:
                return null;
            case SubagentAccess.BLOCKED:
                return `${toolName} changes the whole video, and other subagents are editing it alongside you. Leave it to the agent that started you: say in your report what should change.`;
            case SubagentAccess.UNAVAILABLE:
                return `${toolName} isn't available to subagents.`;
            case SubagentAccess.EDITS_TARGET:
                return this.checkTargets(args);
            case SubagentAccess.PLACEMENT:
                return (
                    this.checkTargets(args) ??
                    this.checkPlacement(toolName, args.after_scene_group_id)
                );
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private checkTargets(args: Record<string, any>): string | null {
        const targetSceneGroupIds = [
            ...this.sceneGroupIdsOfScenes([args.scene_id, ...(args.scene_ids ?? [])]),
            ...this.resolveSceneGroupIds([args.scene_group_id]),
        ];
        const outside = targetSceneGroupIds.find((id) => !this.owns(id));
        if (!outside) {
            return null;
        }
        return `Scene group ${sceneGroupDisplayId(this.luminary, outside)} isn't yours: another subagent is working on it. You may edit only your own scene groups (${this.describe()}) and any you add. Leave it alone, and say in your report if it needs changing.`;
    }

    /**
     * Why placing a scene group there would land it outside this subagent's part of the video. One
     * that owns no scene groups yet has nothing to place after, so its first one may go anywhere.
     */
    private checkPlacement(toolName: string, afterSceneGroupId: unknown): string | null {
        const ownedPresent = this.getSceneGroupIds().filter((id) =>
            this.luminary.getSceneGroupById(id),
        );
        if (ownedPresent.length === 0) {
            return null;
        }
        if (typeof afterSceneGroupId !== 'string' || afterSceneGroupId.trim() === '') {
            return `${toolName} without after_scene_group_id places the scene group at the very start of the video, outside your part. Pass after_scene_group_id set to one of your own scene groups (${this.describe()}).`;
        }
        const [anchor] = this.resolveSceneGroupIds([afterSceneGroupId]);
        if (!anchor || this.owns(anchor)) {
            return null;
        }
        return `Scene group ${sceneGroupDisplayId(this.luminary, anchor)} isn't yours, so don't place scene groups after it. Add or move your scene groups only right after one of your own (${this.describe()}), so your part of the video stays together.`;
    }

    private sceneGroupIdsOfScenes(sceneIds: unknown[]): string[] {
        return sceneIds.flatMap((sceneId) => {
            if (typeof sceneId !== 'string') {
                return [];
            }
            const resolved = resolveSceneById(this.luminary, this.getState(), sceneId);
            const sceneGroupId = 'error' in resolved ? undefined : resolved.scene.sceneGroup?.id;
            return sceneGroupId ? [sceneGroupId] : [];
        });
    }

    private resolveSceneGroupIds(sceneGroupIds: unknown[]): string[] {
        return sceneGroupIds.flatMap((sceneGroupId) => {
            if (typeof sceneGroupId !== 'string') {
                return [];
            }
            const resolved = resolveSceneGroupById(this.luminary, sceneGroupId);
            return 'error' in resolved ? [] : [resolved.sceneGroup.id];
        });
    }
}
